import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useInspections } from "../../api/inspections";
import { Segmented } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import InspectionList from "./InspectionList";
import InspectionWorkspace from "./InspectionWorkspace";
import RaiseInspection from "./RaiseInspection";

/** The short names the rail's switch has room for. */
const SHORT_WORK_TYPE: Record<string, string> = { assessment: "Assess", service: "Service", replacement: "Replace" };

/** Inspections: the list down the left, narrowed by work type and search,
 * with the office's "raise one"; the chosen inspection's checklist on the
 * right. Field operators see only their own. On a phone the list gives way
 * to the open checklist, which carries the way back. The filter lives in the address, so a
 * link back to the list comes back to the same list. */
export default function InspectionsPage() {
  const { id } = useParams();
  const selected = id === undefined ? null : Number(id);
  const [params, setParams] = useSearchParams();
  const filter = { work_type: params.get("work_type") ?? "", q: params.get("q") ?? "" };
  const list = useInspections(filter);
  const data = list.data;
  const [raising, setRaising] = useState(false);
  const [text, setText] = useState(filter.q);
  const settled = useDebounced(text, 300);

  function set(key: "work_type" | "q", value: string) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: key === "q" },
    );
  }

  // The search box writes to the address once the typing pauses; only the
  // settled text triggers it, never a change of address.
  useEffect(() => {
    if (settled.trim() !== filter.q) {
      set("q", settled.trim());
    }
  }, [settled]);

  const workTypes = [{ value: "", label: "All" }, ...(data?.work_types ?? []).map((wt) => ({ value: wt.value, label: SHORT_WORK_TYPE[wt.value] ?? wt.label }))];
  const listSearch = new URLSearchParams(Object.entries(filter).filter(([, v]) => v !== "")).toString();

  return (
    <div className={selected === null ? "page-bleed split inspections-page" : "page-bleed split inspections-page has-selection"}>
      <aside className="split-rail insp-rail" aria-label="Inspections">
        <div className="insp-rail-head">
          <div className="insp-rail-title">
            <h2>Inspections</h2>
            <span className="insp-rail-count">{data?.rows.length ?? ""}</span>
            {data ? (
              <button type="button" className="btn btn-secondary insp-raise-toggle" aria-expanded={raising} onClick={() => setRaising((r) => !r)}>
                {raising ? "Close" : "+ Raise one"}
              </button>
            ) : null}
          </div>
          {raising && data ? (
            <RaiseInspection
              contracts={data.contracts}
              workTypes={data.work_types}
              pm41WorkTypes={data.pm41_work_types}
              office={data.office}
              defaultKind={data.default_kind}
              onRaised={() => setRaising(false)}
            />
          ) : null}
          <input
            type="search"
            className="search-input"
            placeholder="Search works order, chamber or site"
            aria-label="Search inspections"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Segmented fill label="Filter by work type" options={workTypes} value={filter.work_type} onChange={(v) => set("work_type", v)} />
        </div>
        {list.isPending ? <p className="empty-state">Loading inspections…</p> : null}
        {list.isError ? <p className="notice bad insp-rail-notice">The inspections could not be loaded: {list.error.message}</p> : null}
        {data && data.rows.length === 0 ? (
          <p className="empty-state">{filter.q || filter.work_type ? "No inspections match." : "No inspections yet. Raise one, or accept a route plan."}</p>
        ) : null}
        {data && data.rows.length > 0 ? <InspectionList rows={data.rows} selected={selected} search={listSearch} /> : null}
      </aside>
      <section className="split-main insp-main" aria-label="Inspection">
        {selected === null ? (
          <div className="empty-state">
            <strong>Choose an inspection on the left</strong>
            <p>Or raise one from a works order.</p>
          </div>
        ) : (
          <InspectionWorkspace key={selected} id={selected} />
        )}
      </section>
    </div>
  );
}
